/**
 * What the reader's warning codes mean to the person looking at the review screen.
 *
 * The service returns stable codes and never a value read from the document. Until now the modal
 * rendered none of them: it showed one generic sentence whenever validation was less than fully
 * valid, and nothing at all otherwise. Physical acceptance on 2026-09-03 showed what that costs —
 * an operator saw three fields come back empty from a document that plainly carried all three, with
 * no explanation on the screen and the actual reason (an unsupported zone layout) visible only in a
 * service log they have no access to.
 *
 * So this module maps every code the service can produce onto a severity and a translation key.
 * Three properties matter and are tested:
 *
 * - **Every code is renderable.** An unrecognised one falls back to a generic sentence that names
 *   the code rather than being dropped, because a code that nobody mapped is exactly the one worth
 *   seeing.
 * - **Codes carry no personal data**, by the service's construction, so showing one is safe.
 * - **Severity is about what the operator should do**, not about how the service felt: `error` is
 *   "do not trust this value", `warning` is "check this value", `info` is "this is how the document
 *   works, and it is normal".
 */

/** How much attention a code deserves. */
export type ScanWarningSeverity = 'error' | 'warning' | 'info';

/** One code, ready to render. */
export interface ScanWarningNotice {
    readonly code: string;
    readonly severity: ScanWarningSeverity;
    /** i18next key. Absent from the bundle means the code was not one this build knows. */
    readonly translationKey: string;
}

/**
 * The expiry warning is rendered by its own alert, from `documentExpired`.
 *
 * Listed here so it is skipped rather than shown twice — the same fact stated in two places reads
 * as two problems.
 */
const RENDERED_ELSEWHERE = new Set(['DOCUMENT_EXPIRED']);

/**
 * Codes that mean a value must not be trusted.
 *
 * Every one of these is a disagreement between two independent reads of the same characters, or a
 * failed arithmetic check over them. A value behind one of these can be a different person's.
 */
const ERRORS = new Set([
    'MRZ_CHECK_DIGIT_FAILED',
    'VENDOR_DISAGREES_WITH_MRZ',
    'VENDOR_VALIDATION_FAILED',
    'CNP_MALFORMED',
    'CNP_CHECKSUM_FAILED',
    'CNP_COUNTY_NOT_ISSUED',
    'CNP_DATE_NOT_A_CALENDAR_DATE',
    'CNP_DATE_DISAGREES_WITH_MRZ',
    'CNP_SEX_DISAGREES_WITH_MRZ',
    'CNP_SEX_NOT_STATED',
]);

/**
 * Codes that mean something is unproven rather than wrong.
 *
 * The distinction is the whole point of having more than one severity: "the reader could not verify
 * this" and "the reader verified this and it is wrong" call for different actions, and collapsing
 * them into one colour teaches an operator to ignore both.
 */
const WARNINGS = new Set([
    'MRZ_NOT_SUPPORTED_FORMAT',
    'MRZ_DATE_NOT_INDEPENDENTLY_VALIDATED',
    'CNP_SOURCE_NOT_VALIDATED',
    'CNP_CENTURY_NOT_ENCODED',
    'DOCUMENT_TYPE_UNRECOGNISED',
    'CODELINE_LINE_COUNT_OUT_OF_RANGE',
    'CODELINE_LINE_COUNT_MISMATCH',
    'CODELINE_CHECK_DIGIT_COUNT_OUT_OF_RANGE',
    'CODELINE_VALIDATION_OUT_OF_RANGE',
    'CODELINE_IMAGE_SOURCE_OUT_OF_RANGE',
    'CODELINE_DATE_OF_BIRTH_IMPLAUSIBLE',
    'CODELINE_EXPIRY_DATE_IMPLAUSIBLE',
    'CODELINE_NON_BOOLEAN_FLAG',
    'CODELINE_NON_ASCII_TEXT',
]);

/**
 * Codes that describe how the document works.
 *
 * True of every read of its kind, and worth saying once. `MRZ_NAMES_TRANSLITERATED` is the whole
 * category: a machine-readable zone is A-Z, so a name that had diacritics does not have them here,
 * and an operator putting that name on a contract needs to know that before they accept it.
 */
const INFORMATION = new Set(['MRZ_NAMES_TRANSLITERATED']);

/** Most serious first, so the list reads top-down in the order an operator should act. */
const ORDER: readonly ScanWarningSeverity[] = ['error', 'warning', 'info'];

/**
 * Every code this build has a sentence for.
 *
 * A code outside this set still renders — under a generic sentence that names it — because the one
 * a newer service invented is exactly the one worth seeing rather than swallowing.
 */
export const KNOWN_WARNING_CODES: readonly string[] = [...ERRORS, ...WARNINGS, ...INFORMATION];

/** The sentence shown for a code this build does not recognise. It interpolates `code`. */
export const UNRECOGNISED_WARNING_KEY = 'documentScan.warning.unrecognised';

const KNOWN = new Set(KNOWN_WARNING_CODES);

function severityOf(code: string): ScanWarningSeverity {
    if (ERRORS.has(code)) {
        return 'error';
    }

    if (INFORMATION.has(code)) {
        return 'info';
    }

    // Everything else, recognised or not. An unfamiliar code is not evidence of a serious fault,
    // and it is not evidence of nothing either, so it gets the middle severity rather than being
    // dropped or promoted.
    return 'warning';
}

/**
 * Turns the service's codes into notices, most serious first.
 *
 * Duplicates are collapsed — the same code can arrive from the decoder and from the comparison —
 * and the order within a severity is the order the service reported them, which is the order the
 * pipeline noticed them in.
 */
export function describeScanWarnings(
    warnings: readonly string[] | undefined,
): readonly ScanWarningNotice[] {
    if (warnings === undefined) {
        return [];
    }

    const seen = new Set<string>();
    const notices: ScanWarningNotice[] = [];

    for (const code of warnings) {
        if (code === '' || seen.has(code) || RENDERED_ELSEWHERE.has(code)) {
            continue;
        }

        seen.add(code);

        notices.push({
            code,
            severity: severityOf(code),
            translationKey: KNOWN.has(code)
                ? `documentScan.warning.${code}`
                : UNRECOGNISED_WARNING_KEY,
        });
    }

    return [...notices].sort((left, right) => ORDER.indexOf(left.severity) - ORDER.indexOf(right.severity));
}

/** The notices of one severity, for a single grouped alert. */
export function noticesOfSeverity(
    notices: readonly ScanWarningNotice[],
    severity: ScanWarningSeverity,
): readonly ScanWarningNotice[] {
    return notices.filter(notice => notice.severity === severity);
}

/** Every severity that has something to show, most serious first. */
export function severitiesPresent(
    notices: readonly ScanWarningNotice[],
): readonly ScanWarningSeverity[] {
    return ORDER.filter(severity => notices.some(notice => notice.severity === severity));
}
